<script setup lang="ts">
import { LibraryContentObject } from './library_types'
import GoalCard from './GoalCard.vue'
import StrategyCard from './StrategyCard.vue'
import PresentLevelsCard from './PresentLevelsCard.vue'
import PresentLevelsSubjectSummaryCard from './PresentLevelsSubjectSummaryCard.vue'
import ImpactOfDisabilityCard from './ImpactOfDisabilityCard.vue'
import ResourceCard from '@/resources/ResourceCard.vue'
import ViewMoreButton from './ViewMoreButton.vue'
import { ref, PropType, computed } from 'vue'

const props = defineProps({
  contentObjects: {
    type: Array as PropType<Array<LibraryContentObject>>,
    required: true
  },
  contentTypeTitle: {
    type: String,
    required: true
  },
  numberOfItems: {
    type: Number,
    default: 3
  },
  gridView: {
    type: Boolean,
    default: true
  },
  removableContents: {
    type: Boolean,
    default: true
  },
  showContentTypeTitle: {
    type: Boolean,
    default: true
  },
  showAll: {
    type: Boolean,
    default: false
  },
  saved: {
    type: Boolean,
    default: false
  },
  clickable: {
    type: Boolean,
    default: true
  },
  shared: {
    type: Boolean,
    default: false
  }
})

const viewMore = ref(true)
const contentType = computed((): string | null => {
  return props.contentTypeTitle.toLowerCase().replaceAll(' ', '')
})
const columnClasses = computed((): string => {
  return props.numberOfItems === 3
    ? 'sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
    : 'md:grid-cols-1 lg:grid-cols-2'
})
</script>

<template>
  <div>
    <h2 v-if="props.showContentTypeTitle" class="pb-3 pt-6 text-display-sm text-gray-900" data-cy="content-type-header">
      {{ props.contentTypeTitle }}
      <span class="ml-0 text-gray-700">{{ props.contentObjects.length }}</span>
    </h2>
    <p v-if="!props.contentObjects.length">
      <slot name="noContentMessage"></slot>
    </p>
    <div :class="gridView ? 'grid gap-x-2 gap-y-3 pb-3 ' + columnClasses : 'grid gap-y-0 grid-cols-1 auto-rows-fr'">
      <template
        v-for="contentObject in viewMore && !props.showAll
          ? props.contentObjects.slice(0, props.numberOfItems)
          : props.contentObjects"
      >
        <PresentLevelsCard
          v-if="contentType === 'presentlevels' && contentObject.content_type === 'presentlevel'"
          :key="contentObject.content_uuid"
          :presentLevel="contentObject"
          v-bind:removable="removableContents"
          v-bind:clickable="clickable"
          :shared="props.shared"
        />
        <PresentLevelsSubjectSummaryCard
          v-if="contentType === 'presentlevels' && contentObject.content_type === 'pl_student_summary'"
          :key="contentObject.content_uuid"
          :savedSubjectSummary="contentObject"
          v-bind:removable="removableContents"
          v-bind:clickable="clickable"
          :shared="props.shared"
        />
        <GoalCard
          v-if="contentType === 'goals'"
          :key="contentObject.content_uuid"
          :goal="contentObject"
          v-bind:removable="removableContents"
          :saved="props.saved"
          v-bind:clickable="clickable"
          :shared="props.shared"
        />
        <StrategyCard
          v-if="contentType === 'strategies'"
          :key="contentObject.content_uuid"
          :strategy="contentObject"
          v-bind:removable="removableContents"
          :saved="props.saved"
          v-bind:clickable="clickable"
          :shared="props.shared"
          :gridView="gridView"
        />
        <ImpactOfDisabilityCard
          v-if="contentType === 'impactofdisability'"
          :key="contentObject.content_uuid"
          :savedStatement="contentObject"
          v-bind:removable="removableContents"
          v-bind:clickable="clickable"
          :shared="props.shared"
        />
        <ResourceCard
          v-if="contentType === 'resources'"
          :key="contentObject.content_uuid"
          v-bind="contentObject.content_body"
          :collectionUuid="contentObject.collection_uuid"
          :shared="shared"
          v-bind:removable="removableContents"
        />
      </template>
    </div>
    <ViewMoreButton v-if="props.contentObjects.length > props.numberOfItems && !props.showAll" v-model="viewMore" :contentType="contentTypeTitle" />
  </div>
</template>
